import { forwardRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Heart } from "lucide-react";

const CertificatePreview = forwardRef(({ toy, buyer, certificateNumber, message, dateOverride }, ref) => {
  const { t, lang } = useLanguage();
  const c = t.certificate;
  const species = lang === "fr" ? toy?.species_fr : toy?.species_en;
  const personality = lang === "fr" ? toy?.personality_fr : toy?.personality_en;
  const fullName =
    buyer?.isGift && (buyer?.giftRecipient || "").trim()
      ? buyer.giftRecipient.trim()
      : `${buyer?.firstName || "____________"} ${buyer?.lastName || "____________"}`.trim();

  const today = dateOverride
    ? new Date(dateOverride)
    : new Date();
  const dateLocale = lang === "fr" ? "fr-FR" : "en-US";
  const dateStr = today.toLocaleDateString(dateLocale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div
      ref={ref}
      data-testid="certificate-preview"
      className="certificate-bg relative w-full max-w-2xl mx-auto aspect-[1/1.35] p-10 md:p-14 border border-camel/40 shadow-soft"
      style={{
        boxShadow:
          "0 0 0 1px rgba(176, 91, 59, 0.15), 0 0 0 8px #FBF6E9, 0 0 0 9px rgba(176, 91, 59, 0.25), 0 20px 50px rgba(58, 48, 42, 0.12)",
      }}
    >
      {/* Decorative corners */}
      <div className="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 border-terracotta/40" />
      <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-terracotta/40" />
      <div className="absolute bottom-4 left-4 w-12 h-12 border-b-2 border-l-2 border-terracotta/40" />
      <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-terracotta/40" />

      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 text-terracotta mb-2">
          <span className="h-px w-12 bg-terracotta/40" />
          <Heart size={14} fill="currentColor" />
          <span className="h-px w-12 bg-terracotta/40" />
        </div>
        <p className="text-[10px] uppercase tracking-[0.4em] text-camel">{c.subtitle}</p>
        <h2 className="font-serif-display text-3xl md:text-4xl text-ink mt-2 leading-tight">
          {c.title}
        </h2>
        <p className="font-script text-2xl text-terracotta mt-1">N° {certificateNumber || "—"}</p>
      </div>

      {/* Body */}
      <div className="mt-8 text-center space-y-4">
        <p className="font-body text-sm text-ink/70">{c.certifies}</p>
        <p className="font-serif-display text-2xl md:text-3xl text-ink border-b border-dashed border-camel/50 pb-2 inline-block px-6 min-w-[60%]">
          {fullName || "____________"}
        </p>
        <p className="font-body text-sm text-ink/70">{c.hasAdopted}</p>
        <p className="font-serif-display text-3xl md:text-4xl text-terracotta">
          {toy?.name || "—"} <span className="font-script text-camel text-xl">— {species}</span>
        </p>
      </div>

      {/* Details grid */}
      <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-xs font-body">
        <div>
          <div className="uppercase tracking-widest text-camel text-[10px] mb-0.5">{c.bornOn}</div>
          <div className="text-ink">{toy?.birth_date || "—"}</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-camel text-[10px] mb-0.5">{c.tookHours}</div>
          <div className="text-ink">{toy?.creation_hours}h</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-camel text-[10px] mb-0.5">{c.colorsLabel}</div>
          <div className="text-ink">{(toy?.colors || []).join(" · ")}</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-camel text-[10px] mb-0.5">{c.personalityLabel}</div>
          <div className="text-ink">{personality || "—"}</div>
        </div>
      </div>

      {/* Promise / message */}
      <div className="mt-6 px-2">
        {message ? (
          <p className="font-script text-xl text-center text-ink/80 leading-tight">"{message}"</p>
        ) : (
          <p className="font-body text-xs text-center text-ink/60 italic leading-relaxed">{c.promise}</p>
        )}
      </div>

      {/* Footer signature */}
      <div className="absolute bottom-12 left-0 right-0 px-10 md:px-14 flex items-end justify-between">
        <div className="text-[10px] uppercase tracking-widest text-camel">
          <div>{c.signedAt}</div>
          <div className="text-ink font-body normal-case tracking-normal text-sm mt-1">{dateStr}</div>
        </div>
        <div className="text-right">
          <div className="font-script text-4xl text-terracotta leading-none">{c.signature}</div>
          <div className="h-px bg-ink/30 w-32 mt-1 ml-auto" />
          <div className="text-[10px] uppercase tracking-widest text-camel mt-1">Atelier</div>
        </div>
      </div>
    </div>
  );
});

CertificatePreview.displayName = "CertificatePreview";

export default CertificatePreview;
